import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, PenLine, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Editor WYSIWYG: mostra o PDF do contrato de adesão com os campos editáveis
// nas posições reais (AcroForm) para o comercial preencher diretamente.
export const PdfContractFillModal = ({ open, onClose, documentId, documentName, leadId, onGenerated }) => {
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(1);
  const [pageImgs, setPageImgs] = useState({});
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [hasSepa, setHasSepa] = useState(false);
  const [includeSepa, setIncludeSepa] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [pageH, setPageH] = useState(1000);
  const [pageW, setPageW] = useState(800);
  const firstPageRef = useRef(null);
  const measureRef = useRef(null);
  const charWidth = (fs) => {
    if (!measureRef.current) measureRef.current = document.createElement('canvas');
    const ctx = measureRef.current.getContext('2d');
    ctx.font = `${fs}px ui-monospace, Menlo, Consolas, monospace`;
    return ctx.measureText('0').width || fs * 0.6;
  };

  useEffect(() => {
    if (!open || !documentId) return;
    setLoading(true); setLink(''); setValues({});
    (async () => {
      try {
        const [ff, info] = await Promise.all([
          axios.get(`${API_URL}/knowledge/${documentId}/form-fields`),
          axios.get(`${API_URL}/knowledge/${documentId}/pdf-info`).catch(() => ({ data: { page_count: 1 } })),
        ]);
        const cfg = ff.data?.form_fields || [];
        const cfgMap = {};
        cfg.forEach((c) => { cfgMap[c.name] = c; });
        const sigWidget = ff.data?.signature_widget || null;
        const allAcro = (ff.data?.fields || []).filter((f) => !f.is_signature);
        const prettify = (nm) => (nm || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
        let merged;
        if (cfg.length > 0) {
          // Configuração explícita: mostra só os campos escolhidos
          merged = allAcro.filter((f) => cfgMap[f.name]).map((f) => ({ ...f, label: cfgMap[f.name]?.label || prettify(f.name), group: cfgMap[f.name]?.group || null }));
        } else {
          // Sem configuração: todos os campos do PDF (exceto o de assinatura)
          merged = allAcro.filter((f) => f.name && f.name !== sigWidget).map((f) => ({ ...f, label: prettify(f.name), group: null }));
        }
        setFields(merged);
        setHasSepa(merged.some((f) => f.group === 'sepa'));
        const pc = info.data?.page_count || 1;
        setPageCount(pc);
        const imgs = {};
        for (let i = 0; i < pc; i++) {
          try {
            const r = await axios.get(`${API_URL}/knowledge/${documentId}/page-image?page=${i}`, { responseType: 'blob' });
            imgs[i] = URL.createObjectURL(r.data);
          } catch { /* ignore */ }
        }
        setPageImgs(imgs);
      } catch {
        toast.error('Falha ao abrir o contrato');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentId]);

  useEffect(() => () => { Object.values(pageImgs).forEach((u) => URL.revokeObjectURL(u)); }, [pageImgs]);

  const onImgLoad = useCallback(() => {
    if (firstPageRef.current) {
      setPageH(firstPageRef.current.clientHeight || 1000);
      setPageW(firstPageRef.current.clientWidth || 800);
    }
  }, []);

  const setVal = (key, v) => setValues((p) => ({ ...p, [key]: v }));

  // Radio: só um por grupo. Clicar no já selecionado desmarca.
  const setRadio = (f) => setValues((p) => {
    const wasOn = !!p[f.key];
    const next = { ...p };
    fields.forEach((g) => { if (g.radio_group && g.radio_group === f.radio_group) next[g.key] = false; });
    next[f.key] = !wasOn;
    return next;
  });

  // Constrói o payload por NOME do campo, com os valores de exportação para checkbox/radio.
  const buildFieldValues = () => {
    const fv = {};
    fields.forEach((f) => {
      if (f.group === 'sepa' && !includeSepa) return;
      const v = values[f.key];
      if (f.type === 'checkbox' || f.type === 'radio') {
        if (v) fv[f.name] = f.on || 'Yes';
      } else if (v != null && String(v) !== '') {
        fv[f.name] = String(v);
      }
    });
    return fv;
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/leads/${leadId}/signature-requests`, {
        document_id: documentId, field_values: buildFieldValues(), include_sepa: includeSepa,
      });
      const token = res.data.token;
      const full = `${window.location.origin}/sign/${token}`;
      let short = full;
      try {
        const s = await axios.post(`${API_URL}/shorten`, { url: full });
        short = s.data?.short_url || full;
      } catch { /* ignore */ }
      setLink(short);
      toast.success('Contrato preenchido e link de assinatura gerado');
      onGenerated && onGenerated();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Falha ao gerar o link');
    } finally {
      setGenerating(false);
    }
  };

  const fontFor = (f) => Math.max(8, Math.min(18, pageH * f.h * 0.72));
  const visibleFields = fields.filter((f) => f.group !== 'sepa' || includeSepa);
  // Ordena os campos pela leitura visual (linha a linha, esquerda->direita)
  // para que a tecla Tab salte para o campo seguinte correto.
  const sortReading = (arr) => [...arr].sort((a, b) => {
    const ay = Math.round(a.y / 0.02), by = Math.round(b.y / 0.02);
    if (ay !== by) return ay - by;
    return a.x - b.x;
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !generating) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col p-0" data-testid="pdf-contract-fill-modal">
        <DialogHeader className="px-5 pt-4 pb-2 border-b">
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="w-5 h-5 text-indigo-600" /> Preencher contrato — {documentName}
          </DialogTitle>
          <p className="text-xs text-slate-500">Escreva diretamente sobre o contrato de adesão. No fim, gere o link de assinatura para enviar ao cliente.</p>

        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-96"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-3 bg-slate-100" data-testid="pdf-contract-pages">
            {hasSepa && (
              <label className="flex items-center gap-2 text-sm text-cyan-800 bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2 cursor-pointer mb-3 w-fit sticky top-0 z-20" data-testid="fill-sepa-toggle">
                <input type="checkbox" className="h-4 w-4 accent-cyan-600" checked={includeSepa} onChange={(e) => setIncludeSepa(e.target.checked)} />
                Com IBAN (autorização SEPA / débito direto)
              </label>
            )}
            {Array.from({ length: pageCount }).map((_, pi) => (
              <div
                key={pi}
                ref={pi === 0 ? firstPageRef : null}
                className="relative w-full mx-auto mb-4 shadow bg-white"
                style={{ maxWidth: 800 }}
              >
                {pageImgs[pi] && <img src={pageImgs[pi]} alt={`Página ${pi + 1}`} className="w-full block" draggable={false} onLoad={pi === 0 ? onImgLoad : undefined} />}
                {sortReading(visibleFields.filter((f) => f.page === pi)).map((f) => {
                  const style = {
                    left: `${f.x * 100}%`, top: `${f.y * 100}%`,
                    width: `${f.w * 100}%`, height: `${f.h * 100}%`,
                    fontSize: `${fontFor(f)}px`,
                  };
                  if (f.type === 'checkbox' || f.type === 'radio') {
                    const on = !!values[f.key];
                    const isRadio = f.type === 'radio';
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => (isRadio ? setRadio(f) : setVal(f.key, !on))}
                        title={f.label}
                        className={`absolute flex items-center justify-center border ${isRadio ? 'rounded-full' : 'rounded-sm'} ${on ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white/70 border-indigo-400'}`}
                        style={style}
                        data-testid={`fill-cb-${f.key}`}
                      >
                        {on ? (isRadio
                          ? <span style={{ width: '55%', height: '55%' }} className="rounded-full bg-white" />
                          : <Check style={{ width: '90%', height: '90%' }} />) : null}
                      </button>
                    );
                  }
                  if (f.comb && f.maxlen > 1) {
                    // Campo 'comb' (quadradinhos): centra cada caractere no seu quadrado (sem desvio).
                    const fieldPx = Math.max(1, pageW * f.w);
                    const cell = fieldPx / f.maxlen;
                    const cw = charWidth(fontFor(f));
                    const ls = Math.max(0, cell - cw);
                    const combStyle = {
                      ...style,
                      width: `${fieldPx + ls + 2}px`,
                      fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                      letterSpacing: `${ls}px`,
                      paddingLeft: `${Math.max(0, (cell - cw) / 2)}px`,
                      paddingRight: 0,
                      paddingTop: 0,
                      paddingBottom: 0,
                      textAlign: 'left',
                      textTransform: 'uppercase',
                    };
                    return (
                      <input
                        key={f.key}
                        maxLength={f.maxlen}
                        value={values[f.key] || ''}
                        onChange={(e) => setVal(f.key, e.target.value.toUpperCase())}
                        title={f.label}
                        className="absolute bg-transparent border-0 focus:bg-indigo-50/40 outline-none text-slate-900 text-left"
                        style={combStyle}
                        data-testid={`fill-input-${f.key}`}
                      />
                    );
                  }
                  return (
                    <input
                      key={f.key}
                      value={values[f.key] || ''}
                      onChange={(e) => setVal(f.key, e.target.value.toUpperCase())}
                      title={f.label}
                      placeholder={f.label}
                      className="absolute bg-yellow-100/60 border border-indigo-300 focus:bg-white focus:border-indigo-500 outline-none px-0.5 rounded-sm text-slate-900 placeholder:text-slate-400/70 uppercase placeholder:normal-case"
                      style={style}
                      data-testid={`fill-input-${f.key}`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="px-5 py-3 border-t bg-white flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          {link ? (
            <div className="flex-1 flex items-center gap-2">
              <input readOnly value={link} className="flex-1 text-sm border rounded-lg px-3 h-10 bg-slate-50" data-testid="fill-generated-link" />
              <Button variant="outline" className="h-10" onClick={() => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button className="h-10 bg-green-600 hover:bg-green-700" onClick={onClose}>Concluir</Button>
            </div>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={generating}>Cancelar</Button>
              <Button onClick={generate} disabled={generating || loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="fill-generate-btn">
                {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PenLine className="w-4 h-4 mr-2" />}
                Gerar Link de Assinatura
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
